import React from 'react';
import PropTypes from 'prop-types';
import Cta from '@components/CTA';
import useSmoothScroll from '../../hooks/useSmoothScroll';

/**
 * ScrollToSection - A component for creating links that smoothly scroll to page sections
 * 
 * @param {Object} props - Component props
 * @param {string} props.sectionId - The ID of the section to scroll to (without the # symbol)
 * @param {React.ReactNode} props.children - The content to display within the link
 * @param {string} props.className - Additional CSS classes
 * @param {Object} props.headerRef - Reference to the header element (for offset calculation)
 * @param {Function} props.onClickCallback - Optional callback to run after click
 * @param {Object} props.ctaProps - Additional props to pass to the Cta component
 * @returns {React.ReactElement} The ScrollToSection component
 */
const ScrollToSection = ({
    sectionId,
    children,
    className = '',
    headerRef,
    onClickCallback,
    ctaProps = {}
}) => {
    const smoothScrollToSection = useSmoothScroll(headerRef);

    const handleClick = (e) => {
        e.preventDefault();

        // Scroll to the section
        smoothScrollToSection(sectionId);

        // Call the callback if provided
        if (onClickCallback && typeof onClickCallback === 'function') {
            onClickCallback(e);
        }
    };

    return (
        <Cta
            displayType="Link"
            href={`#${sectionId}`}
            label={children}
            onClick={handleClick}
            style={className}
            {...ctaProps}
        />
    );
};

ScrollToSection.propTypes = {
    sectionId: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
    headerRef: PropTypes.object.isRequired,
    onClickCallback: PropTypes.func,
    ctaProps: PropTypes.object
};

export default ScrollToSection; 